import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../../../contexts/AuthContext'

interface Settings {
  siteTitle: string
  siteDescription: string
}

export default function DashboardSettings() {
  const { user } = useAuth()
  const [settings, setSettings] = useState<Settings>({
    siteTitle: 'Peyton Clark - Portfolio',
    siteDescription: 'Portfolio website showcasing projects and services',
  })
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      // Try to load from localStorage first (fallback)
      const stored = localStorage.getItem('dashboard_settings')
      if (stored) {
        setSettings(JSON.parse(stored))
      }
      
      // Try to load from Supabase if a settings table exists
      // For now, we'll use localStorage as primary storage
      setLoading(false)
    } catch (error) {
      console.error('Error loading settings:', error)
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Save to localStorage
      localStorage.setItem('dashboard_settings', JSON.stringify(settings))
      
      // Optionally save to Supabase if settings table exists
      // For now, just use localStorage
      
      alert('Settings saved successfully!')
    } catch (error) {
      console.error('Error saving settings:', error)
      alert('Error saving settings. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const springConfig = { type: "spring", stiffness: 60, damping: 10 }

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-semibold text-text-primary mb-6 sm:mb-8">Settings</h2>

      <div className="space-y-8">
        <div className="neu rounded-2xl p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">General</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Site Title
              </label>
              <input
                type="text"
                value={settings.siteTitle}
                onChange={(e) =>
                  setSettings({ ...settings, siteTitle: e.target.value })
                }
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Site Description
              </label>
              <textarea
                value={settings.siteDescription}
                onChange={(e) =>
                  setSettings({ ...settings, siteDescription: e.target.value })
                }
                rows={3}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all resize-none font-normal text-base touch-manipulation"
              />
            </div>
          </div>
        </div>

        <div className="neu rounded-2xl p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">Account</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary/50 cursor-not-allowed touch-manipulation"
              />
              <p className="text-xs text-text-primary/50 mt-1">
                Email cannot be changed from this interface
              </p>
            </div>
          </div>
        </div>

        <div className="neu rounded-2xl p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">Payment Integration</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-text-primary/70 mb-4">
                Stripe integration is configured for payments. Checkout is triggered from your dashboard or shared links; the API endpoint is used for creating checkout sessions.
              </p>
              <div className="space-y-2 text-sm">
                <p className="text-text-primary/70">
                  <span className="font-medium text-text-primary">Publishable Key:</span> Configured via environment variables
                </p>
                <p className="text-text-primary/70">
                  <span className="font-medium text-text-primary">Secret Key:</span> Configured in Vercel Dashboard (for serverless functions)
                </p>
                <p className="text-text-primary/70">
                  <span className="font-medium text-text-primary">API Endpoint:</span> /api/create-checkout-session
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <motion.button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-5 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-medium text-text-primary neu-hover touch-manipulation text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={saving ? {} : { scale: 1.02, y: -1 }}
            whileTap={saving ? {} : { scale: 0.98 }}
            transition={springConfig}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </motion.button>
        </div>
      </div>
    </div>
  )
}


